import React from "react";
import styled from "styled-components";
import { Link, useNavigate, useLocation } from "react-router-dom";

const FooterContainer = styled.footer`
  background: ${(props) => props.theme.colors.primary};
  color: white;
  padding: 3rem 2rem 1.5rem;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }
`;

const FooterSection = styled.div``;

const FooterTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 1rem;
  font-family: ${(props) => props.theme.fonts.heading};
`;

const FooterText = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  opacity: 0.9;
  margin-bottom: 1rem;
`;

const FooterList = styled.ul`
  list-style: none;
`;

const FooterListItem = styled.li`
  margin-bottom: 0.75rem;
`;

const FooterLink = styled.a`
  color: white;
  font-size: 0.95rem;
  opacity: 0.9;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 1;
  }
`;

const FooterRouterLink = styled(Link)`
  color: white;
  font-size: 0.95rem;
  opacity: 0.9;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 1;
  }
`;

const Footer: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    e.preventDefault();

    // If we're not on the home page, navigate to home first
    if (location.pathname !== '/') {
      navigate('/');
      // Wait for navigation to complete, then scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      // If already on home page, just scroll
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  return (
    <FooterContainer role="contentinfo" aria-label="כותרת תחתונה">
      <FooterContent>
        <FooterSection>
          <FooterTitle>לי פרוינד - פסיכולוגית חינוכית מומחית</FooterTitle>
          <FooterText>
            מרחב בטוח לליווי רגשי, עם גישה מקצועית ואמפתית לילדים ומשפחות
          </FooterText>
        </FooterSection>

        <FooterSection>
          <FooterTitle>קישורים מהירים</FooterTitle>
          <FooterList role="navigation" aria-label="ניווט תחתון">
            <FooterListItem>
              <FooterLink href="#home" onClick={(e) => scrollToSection(e, 'home')} aria-label="חזור לעמוד הבית">בית</FooterLink>
            </FooterListItem>
            <FooterListItem>
              <FooterLink href="#about" onClick={(e) => scrollToSection(e, 'about')} aria-label="עבור לקטע אודות">אודות</FooterLink>
            </FooterListItem>
            <FooterListItem>
              <FooterLink href="#services" onClick={(e) => scrollToSection(e, 'services')} aria-label="עבור לקטע שירותים">שירותים</FooterLink>
            </FooterListItem>
            <FooterListItem>
              <FooterLink href="#contact" onClick={(e) => scrollToSection(e, 'contact')} aria-label="עבור לקטע צור קשר">צור קשר</FooterLink>
            </FooterListItem>
          </FooterList>
          <FooterTitle style={{ marginTop: '1.5rem' }}>משפטי</FooterTitle>
          <FooterList role="navigation" aria-label="קישורים משפטיים">
            <FooterListItem>
              <FooterRouterLink to="/terms-of-use" aria-label="עבור לעמוד תנאי שימוש">תנאי שימוש</FooterRouterLink>
            </FooterListItem>
            <FooterListItem>
              <FooterRouterLink to="/accessibility" aria-label="עבור לעמוד הצהרת נגישות">הצהרת נגישות</FooterRouterLink>
            </FooterListItem>
          </FooterList>
        </FooterSection>

        <FooterSection>
          <FooterTitle>פרטי קשר</FooterTitle>
          <FooterList role="list" aria-label="פרטי יצירת קשר">
            <FooterListItem>
              <FooterText><span aria-label="טלפון">☎</span> <a href="tel:0545954754" aria-label="התקשר למספר 054-5954754">054-5954754</a></FooterText>
            </FooterListItem>
            <FooterListItem>
              <FooterText><span aria-label="דואר אלקטרוני">✉</span> <a href="mailto:leefreund90@gmail.com" aria-label="שלח מייל לכתובת leefreund90@gmail.com">leefreund90@gmail.com</a></FooterText>
            </FooterListItem>
            <FooterListItem>
              <FooterText><span aria-label="מיקום">📍</span> שוהם</FooterText>
            </FooterListItem>
          </FooterList>
        </FooterSection>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer;
